import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Upload,
  Users,
  FileText,
  Clock,
  GraduationCap,
  Loader2,
  Mail,
  Send,
  Filter,
  Download,
  ClipboardList,
  ChevronDown,
  Lightbulb,
  Timer,
  Target,
  GitCompare,
  Sparkles,
  CheckSquare,
  Square,
  Settings2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";
import CandidateCompare from "../components/CandidateCompare";
import ShortlistRecommendation from "../components/ShortlistRecommendation";
import ScoringWeightsEditor from "../components/ScoringWeightsEditor";
import InterviewCandidates from "../components/InterviewCandidates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user, requireEmail } = useUser();
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailFilterMode, setEmailFilterMode] = useState("all");
  const [minMatchPercentage, setMinMatchPercentage] = useState(50);
  const [pendingFile, setPendingFile] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Suggested Assessments state
  const [assessments, setAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [assessmentsOpen, setAssessmentsOpen] = useState(false);
  const [assessmentsLoaded, setAssessmentsLoaded] = useState(false);
  
  // Assessment selection and email state
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [sendAssessmentsDialogOpen, setSendAssessmentsDialogOpen] = useState(false);
  const [ccEmail, setCcEmail] = useState("");
  const [sendingAssessments, setSendingAssessments] = useState(false);
  
  // Candidate selection for comparison
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [shortlistOpen, setShortlistOpen] = useState(false);
  const [weightsEditorOpen, setWeightsEditorOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [jobRes, candidatesRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`),
        axios.get(`${API}/jobs/${jobId}/candidates`),
      ]);
      setJob(jobRes.data);
      setCandidates(candidatesRes.data);
    } catch (error) {
      console.error("Error fetching job:", error);
      toast.error("Puesto no encontrado");
      navigate("/jobs");
    } finally {
      setLoading(false);
    }
  }, [jobId, navigate]);

  // Pre-fill email with user's email when user logs in
  useEffect(() => {
    if (user?.email) {
      setRecipientEmail(user.email);
      setEmailFilterMode(user.email_filter_mode || "all");
      setMinMatchPercentage(user.min_match_percentage || 50);
    }
  }, [user]);

  const handleExportCSV = async () => {
    if (!user) {
      requireEmail(() => handleExportCSV());
      return;
    }
    
    setExporting(true);
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/export/csv`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${job?.title || 'candidatos'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV descargado exitosamente");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Error al exportar CSV");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // State for bulk upload
  const [uploadProgress, setUploadProgress] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const MAX_FILES = 10;

    // Validate file count
    if (fileArray.length > MAX_FILES) {
      toast.error(`Máximo ${MAX_FILES} archivos a la vez. Seleccionaste ${fileArray.length}.`);
      return;
    }

    // Validate file types
    const invalidFiles = fileArray.filter(f => {
      const ext = f.name.toLowerCase().split(".").pop();
      return !["pdf", "docx"].includes(ext);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Archivos no válidos: ${invalidFiles.map(f => f.name).join(", ")}. Solo PDF o DOCX.`);
      return;
    }

    // If user not logged in, save files and ask for email
    if (!user) {
      setPendingFile(fileArray[0]); // Save first file for reference
      requireEmail(() => {
        uploadFiles(fileArray);
      });
      return;
    }

    uploadFiles(fileArray);
  };

  const uploadFiles = async (files) => {
    if (files.length === 1) {
      // Single file - use original endpoint for simplicity
      uploadFile(files[0]);
      return;
    }

    // Multiple files - use bulk endpoint
    setBulkUploading(true);
    setUploadProgress(files.map(f => ({ name: f.name, status: "pending", progress: 0 })));

    const formData = new FormData();
    files.forEach(file => {
      formData.append("files", file);
    });

    try {
      // Update progress to uploading
      setUploadProgress(files.map(f => ({ name: f.name, status: "uploading", progress: 50 })));

      const response = await axios.post(
        `${API}/jobs/${jobId}/candidates/bulk`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const { successful, failed, results } = response.data;

      // Update progress based on results
      setUploadProgress(results.map(r => ({
        name: r.file_name,
        status: r.status === "success" ? "success" : "error",
        progress: 100,
        error: r.error,
        matchScore: r.match_score
      })));

      // Refresh candidates list
      await fetchData();

      // Show summary toast
      if (failed === 0) {
        toast.success(`✅ ${successful} CV(s) subidos y analizados exitosamente`);
      } else if (successful === 0) {
        toast.error(`❌ Error al procesar todos los CVs`);
      } else {
        toast.warning(`${successful} CV(s) exitosos, ${failed} con errores`);
      }

      // Clear progress after a delay
      setTimeout(() => {
        setUploadProgress([]);
      }, 5000);

    } catch (error) {
      console.error("Error in bulk upload:", error);
      setUploadProgress(files.map(f => ({ name: f.name, status: "error", progress: 100, error: error.response?.data?.detail || "Error" })));
      toast.error(error.response?.data?.detail || "Error al subir los CVs");
    } finally {
      setBulkUploading(false);
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/jobs/${jobId}/candidates`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setCandidates([response.data, ...candidates]);
      toast.success("¡CV subido y analizado exitosamente!");
      setPendingFile(null);
    } catch (error) {
      console.error("Error uploading CV:", error);
      toast.error(error.response?.data?.detail || "Error al subir el CV");
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleSendSummary = async () => {
    if (!recipientEmail.trim()) {
      toast.error("Por favor ingresa un email");
      return;
    }

    // Check if filter would return any candidates
    if (emailFilterMode === "above_threshold") {
      const filteredCount = candidates.filter(
        c => (c.match_score?.overall_score || 0) >= minMatchPercentage
      ).length;
      if (filteredCount === 0) {
        toast.error(`No hay candidatos con match ≥ ${minMatchPercentage}%`);
        return;
      }
    }

    setSendingEmail(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/send-summary`, {
        recipient_email: recipientEmail,
        min_match_percentage: minMatchPercentage,
        filter_mode: emailFilterMode,
      });
      toast.success(`Resumen enviado a ${recipientEmail}`);
      setEmailDialogOpen(false);
    } catch (error) {
      console.error("Error sending summary:", error);
      toast.error(error.response?.data?.detail || "Error al enviar el resumen");
    } finally {
      setSendingEmail(false);
    }
  };

  const getMatchBadgeClass = (level) => {
    if (level === "Strong match") return "badge-strong";
    if (level === "Medium match") return "badge-medium";
    return "badge-low";
  };

  const getScoreCircleClass = (score) => {
    if (score >= 75) return "score-strong";
    if (score >= 50) return "score-medium";
    return "score-low";
  };

  // Candidate selection handlers
  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      }
      if (prev.length >= 5) {
        toast.error("Máximo 5 candidatos para comparar");
        return prev;
      }
      return [...prev, candidateId];
    });
  };

  const clearSelection = () => {
    setSelectedCandidates([]);
  };

  const getSelectedCandidatesData = () => {
    return candidates.filter(c => selectedCandidates.includes(c.id));
  };

  // Handler for when weights are updated
  const handleWeightsUpdate = (newWeights) => {
    // Refresh job and candidates data
    fetchData();
  };

  // Calculate how many candidates would be in the email
  const getFilteredCandidateCount = () => {
    if (emailFilterMode === "all") return candidates.length;
    return candidates.filter(
      c => (c.match_score?.overall_score || 0) >= minMatchPercentage
    ).length;
  };

  // Fetch suggested assessments when section is opened
  const fetchAssessments = async () => {
    if (assessmentsLoaded) return;
    
    setLoadingAssessments(true);
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/suggested-assessments`);
      setAssessments(response.data.assessments || []);
      setAssessmentsLoaded(true);
    } catch (error) {
      console.error("Error fetching assessments:", error);
      toast.error("Error al cargar las evaluaciones sugeridas");
    } finally {
      setLoadingAssessments(false);
    }
  };

  // Load assessments when collapsible is opened
  const handleAssessmentsToggle = (open) => {
    setAssessmentsOpen(open);
    if (open && !assessmentsLoaded) {
      fetchAssessments();
    }
  };

  // Toggle assessment selection
  const toggleAssessmentSelection = (index) => {
    setSelectedAssessments(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      return [...prev, index];
    });
  };

  // Select/deselect all assessments
  const toggleSelectAllAssessments = () => {
    if (selectedAssessments.length === assessments.length) {
      setSelectedAssessments([]);
    } else {
      setSelectedAssessments(assessments.map((_, i) => i));
    }
  };

  // Get selected assessments data
  const getSelectedAssessmentsData = () => {
    return selectedAssessments.map(i => assessments[i]);
  };

  // Send assessments email
  const handleSendAssessments = async () => {
    const token = localStorage.getItem("authToken");
    
    // Check if user is logged in with valid token
    if (!user || !token) {
      requireEmail(() => handleSendAssessments());
      return;
    }

    const selectedData = getSelectedAssessmentsData();
    if (selectedData.length === 0) {
      toast.error("Seleccioná al menos una evaluación");
      return;
    }

    setSendingAssessments(true);
    try {
      await axios.post(
        `${API}/jobs/${jobId}/send-assessments`,
        {
          assessments: selectedData,
          cc_email: ccEmail.trim() || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success(`Evaluaciones enviadas a ${user.email}${ccEmail ? ` con copia a ${ccEmail}` : ""}`);
      setSendAssessmentsDialogOpen(false);
      setSelectedAssessments([]);
      setCcEmail("");
    } catch (error) {
      console.error("Error sending assessments:", error);
      // If token error, prompt re-login
      if (error.response?.status === 401) {
        toast.error("Sesión expirada. Por favor iniciá sesión nuevamente.");
        requireEmail(() => handleSendAssessments());
      } else {
        toast.error(error.response?.data?.detail || "Error al enviar las evaluaciones");
      }
    } finally {
      setSendingAssessments(false);
    }
  };

  // Get assessment type badge style
  const getAssessmentTypeStyle = (type) => {
    switch (type?.toLowerCase()) {
      case "práctico":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
      case "cognitivo":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300";
      case "conductual":
        return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
      case "cultural":
        return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300";
    }
  };

  // Get stage badge style
  const getStageStyle = (stage) => {
    switch (stage?.toLowerCase()) {
      case "screening":
        return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
      case "shortlist":
        return "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300";
      case "final":
        return "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-muted rounded" />
        <div className="h-48 bg-muted rounded-2xl" />
        <div className="h-64 bg-muted rounded-2xl" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/jobs")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
              {job.title}
            </h1>
            <p className="text-muted-foreground mt-1 line-clamp-2">
              {job.description || "Sin descripción"}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Export CSV Button */}
          {candidates.length > 0 && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleExportCSV}
              disabled={exporting}
              data-testid="export-csv-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">CSV</span>
            </Button>
          )}
          
          {/* Send Summary Button */}
          {candidates.length > 0 && (
            <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="gap-2" 
                  data-testid="send-summary-btn"
                onClick={(e) => {
                  if (!user) {
                    e.preventDefault();
                    requireEmail(() => {
                      setEmailDialogOpen(true);
                    });
                  }
                }}
              >
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">Enviar Resumen</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Enviar Resumen de Candidatos</DialogTitle>
                <DialogDescription>
                  Envía un email con el resumen de candidatos para este puesto.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-5 py-4">
                {/* Email Input */}
                <div className="space-y-2">
                  <Label>Email del destinatario</Label>
                  <Input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    data-testid="recipient-email-input"
                  />
                </div>

                {/* Filter Mode */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Incluir en el resumen
                  </Label>
                  <RadioGroup
                    value={emailFilterMode}
                    onValueChange={setEmailFilterMode}
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="all" id="email-filter-all" />
                      <Label htmlFor="email-filter-all" className="font-normal cursor-pointer">
                        Todos los {candidates.length} candidatos
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="above_threshold" id="email-filter-threshold" />
                      <Label htmlFor="email-filter-threshold" className="font-normal cursor-pointer">
                        Solo los que superen el % mínimo
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Minimum Match Slider */}
                {emailFilterMode === "above_threshold" && (
                  <div className="space-y-3 p-4 bg-muted rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label>Match mínimo</Label>
                      <span className="text-xl font-heading font-bold text-primary">
                        {minMatchPercentage}%
                      </span>
                    </div>
                    <Slider
                      value={[minMatchPercentage]}
                      onValueChange={(value) => setMinMatchPercentage(value[0])}
                      min={0}
                      max={100}
                      step={5}
                      className="w-full"
                      data-testid="email-min-match-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                    <p className="text-sm text-center">
                      <strong>{getFilteredCandidateCount()}</strong> de {candidates.length} candidatos cumplen
                    </p>
                  </div>
                )}

                {/* Preview */}
                <div className="bg-secondary/50 p-3 rounded-lg text-sm">
                  <p className="font-medium mb-1">El email incluirá:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• {getFilteredCandidateCount()} candidatos ordenados por puntaje</li>
                    <li>• Resumen de habilidades y experiencia</li>
                    <li>• Distribución de niveles de match</li>
                  </ul>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setEmailDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSendSummary}
                  disabled={sendingEmail || getFilteredCandidateCount() === 0}
                  className="gap-2"
                  data-testid="confirm-send-btn"
                >
                  {sendingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {sendingEmail ? "Enviando..." : "Enviar Resumen"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Job Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <FileText className="w-4 h-4" />
              <span className="text-xs">Requeridas</span>
            </div>
            <p className="font-heading font-bold text-lg mt-1">
              {job.required_skills.length} habilidades
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="w-4 h-4" />
              <span className="text-xs">Candidatos</span>
            </div>
            <p className="font-heading font-bold text-lg mt-1">
              {candidates.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4" />
              <span className="text-xs">Experiencia</span>
            </div>
            <p className="font-heading font-bold text-lg mt-1">
              {job.min_years_experience || 0}+ años
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <GraduationCap className="w-4 h-4" />
              <span className="text-xs">Educación</span>
            </div>
            <p className="font-heading font-bold text-sm mt-1 truncate">
              {job.education_level || "Cualquiera"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Scoring Weights Configuration */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <Settings2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-sm">Pesos del Scoring</h3>
                <p className="text-xs text-muted-foreground">
                  {job.scoring_weights ? (
                    <>
                      Skills: {job.scoring_weights.required_skills}% • 
                      Exp: {job.scoring_weights.experience}% • 
                      Edu: {job.scoring_weights.education}% • 
                      Opt: {job.scoring_weights.optional_skills}%
                    </>
                  ) : (
                    "Configuración por defecto (40-30-15-15)"
                  )}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setWeightsEditorOpen(true)}
              className="gap-2 shrink-0"
            >
              <Settings2 className="w-4 h-4" />
              <span className="hidden sm:inline">Configurar</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Suggested Assessments Section */}
      {candidates.length > 0 && (
        <Collapsible open={assessmentsOpen} onOpenChange={handleAssessmentsToggle}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <button className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-heading font-semibold">Evaluaciones Sugeridas</h3>
                    <p className="text-sm text-muted-foreground">
                      Validaciones recomendadas para este proceso de selección
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${assessmentsOpen ? 'rotate-180' : ''}`} />
              </button>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <div className="border-t px-4 pb-4">
                {loadingAssessments ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">Generando evaluaciones...</span>
                  </div>
                ) : assessments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay evaluaciones disponibles
                  </div>
                ) : (
                  <div className="space-y-3 pt-4">
                    {/* Action bar for assessments */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={toggleSelectAllAssessments}
                          className={`
                            shrink-0 flex items-center justify-center
                            w-8 h-8 rounded-md border-2 transition-all
                            ${selectedAssessments.length === assessments.length
                              ? 'bg-primary border-primary text-primary-foreground'
                              : 'bg-background border-muted-foreground/30 hover:border-primary/50'
                            }
                          `}
                          aria-label={selectedAssessments.length === assessments.length ? "Deseleccionar todo" : "Seleccionar todo"}
                        >
                          {selectedAssessments.length === assessments.length ? (
                            <CheckSquare className="w-4 h-4" />
                          ) : (
                            <Square className="w-4 h-4 opacity-40" />
                          )}
                        </button>
                        <span className="text-sm text-muted-foreground">
                          {selectedAssessments.length > 0 ? (
                            <span className="font-medium text-foreground">
                              {selectedAssessments.length} seleccionada{selectedAssessments.length > 1 ? 's' : ''}
                            </span>
                          ) : (
                            "Seleccioná para enviar por email"
                          )}
                        </span>
                      </div>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => setSendAssessmentsDialogOpen(true)}
                        disabled={selectedAssessments.length === 0}
                        className="gap-2 w-full sm:w-auto"
                      >
                        <Send className="w-4 h-4" />
                        Enviar al Analista
                      </Button>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <Lightbulb className="w-4 h-4" />
                      <span>Basadas en los requisitos del puesto y brechas detectadas en candidatos</span>
                    </div>
                    
                    {assessments.map((assessment, index) => {
                      const isSelected = selectedAssessments.includes(index);
                      return (
                        <div
                          key={index}
                          className={`p-4 rounded-lg border bg-card hover:bg-muted/30 transition-all cursor-pointer ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                          onClick={() => toggleAssessmentSelection(index)}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAssessmentSelection(index);
                              }}
                              className={`
                                shrink-0 flex items-center justify-center
                                w-8 h-8 rounded-md border-2 transition-all mt-0.5
                                ${isSelected
                                  ? 'bg-primary border-primary text-primary-foreground'
                                  : 'bg-background border-muted-foreground/30 hover:border-primary/50'
                                }
                              `}
                              aria-label={isSelected ? "Deseleccionar evaluación" : "Seleccionar evaluación"}
                            >
                              {isSelected ? (
                                <CheckSquare className="w-4 h-4" />
                              ) : (
                                <Square className="w-4 h-4 opacity-40" />
                              )}
                            </button>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-2">
                                    <h4 className="font-medium">{assessment.name}</h4>
                                    <Badge className={`text-xs ${getAssessmentTypeStyle(assessment.type)}`}>
                                      {assessment.type}
                                    </Badge>
                                    <Badge variant="outline" className={`text-xs ${getStageStyle(assessment.stage)}`}>
                                      {assessment.stage}
                                    </Badge>
                                  </div>
                                  
                                  <div className="space-y-1.5 text-sm">
                                    <p className="flex items-start gap-2">
                                      <Target className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                                      <span><strong className="text-foreground">Valida:</strong> {assessment.validates}</span>
                                    </p>
                                    <p className="text-muted-foreground">{assessment.relevance}</p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-1.5 text-sm text-muted-foreground shrink-0">
                                  <Timer className="w-4 h-4" />
                                  <span>{assessment.duration}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    <p className="text-xs text-center text-muted-foreground pt-2">
                      Estas sugerencias son generadas por IA basándose en el perfil del puesto.
                    </p>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Send Assessments Dialog */}
      <Dialog open={sendAssessmentsDialogOpen} onOpenChange={setSendAssessmentsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Enviar Evaluaciones
            </DialogTitle>
            <DialogDescription>
              Las evaluaciones seleccionadas se enviarán a tu email. Podés agregar un CC para el gerente contratante.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Selected count */}
            <div className="bg-primary/10 p-3 rounded-lg">
              <p className="text-sm font-medium text-primary">
                {selectedAssessments.length} evaluación{selectedAssessments.length > 1 ? 'es' : ''} seleccionada{selectedAssessments.length > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {getSelectedAssessmentsData().map(a => a.name).join(", ")}
              </p>
            </div>

            {/* Recipient info */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Destinatario principal
              </Label>
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium">{user?.email || "Tu email"}</p>
                <p className="text-xs text-muted-foreground">Las evaluaciones se enviarán a este email</p>
              </div>
            </div>

            {/* CC Email */}
            <div className="space-y-2">
              <Label htmlFor="cc-email" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                CC - Gerente Contratante (opcional)
              </Label>
              <Input
                id="cc-email"
                type="email"
                placeholder="gerente@empresa.com"
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Agregá el email del gerente contratante para enviarle una copia
              </p>
            </div>

            {/* Preview */}
            <div className="bg-secondary/50 p-3 rounded-lg text-sm">
              <p className="font-medium mb-2">El email incluirá:</p>
              <ul className="text-muted-foreground space-y-1">
                <li>• Detalle de cada evaluación seleccionada</li>
                <li>• Tipo, duración y etapa de aplicación</li>
                <li>• Próximos pasos sugeridos</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSendAssessmentsDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSendAssessments}
              disabled={sendingAssessments || selectedAssessments.length === 0}
              className="gap-2"
            >
              {sendingAssessments ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {sendingAssessments ? "Enviando..." : "Enviar Evaluaciones"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs defaultValue="candidates" className="space-y-4">
        <TabsList>
          <TabsTrigger value="candidates" data-testid="candidates-tab">
            Candidatos ({candidates.length})
          </TabsTrigger>
          <TabsTrigger value="interviews" data-testid="interviews-tab">
            Entrevistas
          </TabsTrigger>
          <TabsTrigger value="requirements" data-testid="requirements-tab">
            Requisitos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="candidates" className="space-y-4">
          {/* Upload Zone - Now supports multiple files */}
          <Card data-testid="upload-zone">
            <CardContent className="py-6">
              <div
                className={`upload-zone ${dragActive ? "active" : ""} ${bulkUploading ? "opacity-50 pointer-events-none" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".pdf,.docx"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                  id="cv-upload"
                  disabled={uploading || bulkUploading}
                  multiple
                />
                <label htmlFor="cv-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-3">
                    {(uploading || bulkUploading) ? (
                      <Loader2 className="w-10 h-10 text-primary animate-spin" />
                    ) : (
                      <Upload className="w-10 h-10 text-muted-foreground" />
                    )}
                    <div className="text-center">
                      <p className="font-medium text-foreground">
                        {(uploading || bulkUploading) ? "Analizando CVs..." : "Subir CVs"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Arrastra y suelta o haz clic • PDF, DOCX • <span className="text-primary font-medium">Hasta 10 archivos a la vez</span>
                      </p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Bulk Upload Progress */}
              {uploadProgress.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Progreso de carga:</p>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {uploadProgress.map((item, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center gap-3 p-2 rounded-lg text-sm ${
                          item.status === "success" ? "bg-green-50 dark:bg-green-950/30" :
                          item.status === "error" ? "bg-red-50 dark:bg-red-950/30" :
                          "bg-muted/50"
                        }`}
                      >
                        {item.status === "pending" && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                        {item.status === "uploading" && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
                        {item.status === "success" && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                        {item.status === "error" && <AlertCircle className="w-4 h-4 text-red-600" />}
                        
                        <span className="flex-1 truncate">{item.name}</span>
                        
                        {item.status === "success" && item.matchScore !== undefined && (
                          <Badge variant="outline" className="text-xs">
                            {item.matchScore}% match
                          </Badge>
                        )}
                        {item.status === "error" && item.error && (
                          <span className="text-xs text-red-600 truncate max-w-[150px]">{item.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Candidates List */}
          {candidates.length === 0 ? (
            <Card className="text-center py-12" data-testid="no-candidates">
              <CardContent>
                <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">
                  Sin candidatos aún
                </h3>
                <p className="text-muted-foreground">
                  Sube CVs para empezar a encontrar candidatos
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* Action Bar for Selection - Mobile Optimized */}
              {candidates.length >= 2 && (
                <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 -mx-4 px-4 py-3 sm:mx-0 sm:px-0 sm:py-0 sm:static sm:bg-transparent sm:backdrop-blur-none border-b sm:border-0">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-muted/50 rounded-lg border">
                    <div className="flex items-center justify-between sm:justify-start gap-3">
                      <span className="text-sm text-muted-foreground">
                        {selectedCandidates.length > 0 ? (
                          <span className="font-medium text-foreground">
                            {selectedCandidates.length} seleccionado{selectedCandidates.length > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="hidden sm:inline">Seleccioná candidatos para comparar</span>
                        )}
                      </span>
                      {selectedCandidates.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={clearSelection} className="h-8">
                          Limpiar
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCompareOpen(true)}
                        disabled={selectedCandidates.length < 2}
                        className="gap-2 flex-1 sm:flex-initial h-10 sm:h-8"
                      >
                        <GitCompare className="w-4 h-4" />
                        <span className="hidden xs:inline">Comparar</span> ({selectedCandidates.length}/5)
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => setShortlistOpen(true)}
                        className="gap-2 flex-1 sm:flex-initial h-10 sm:h-8"
                      >
                        <Sparkles className="w-4 h-4" />
                        <span className="hidden xs:inline">Generar</span> Shortlist
                      </Button>
                    </div>
                  </div>
                  
                  {/* Mobile hint */}
                  {selectedCandidates.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center mt-2 sm:hidden">
                      Tocá los cuadros para seleccionar candidatos
                    </p>
                  )}
                </div>
              )}

              {/* Candidates List */}
              {candidates.map((candidate, index) => {
                const isSelected = selectedCandidates.includes(candidate.id);
                
                return (
                <Card 
                  key={candidate.id} 
                  className={`card-hover transition-all ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                  data-testid={`candidate-${candidate.id}`}
                >
                  <CardContent className="py-4 px-3 sm:px-6">
                    <div className="flex items-start gap-2 sm:gap-4">
                      {/* Selection Area - Large touch target for mobile */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleCandidateSelection(candidate.id);
                        }}
                        className={`
                          shrink-0 flex items-center justify-center
                          w-11 h-11 sm:w-8 sm:h-8 
                          rounded-lg sm:rounded-md
                          border-2 transition-all
                          touch-manipulation
                          ${isSelected 
                            ? 'bg-primary border-primary text-primary-foreground' 
                            : 'bg-background border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/50'
                          }
                        `}
                        aria-label={isSelected ? "Deseleccionar candidato" : "Seleccionar candidato"}
                      >
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5 sm:w-4 sm:h-4" />
                        ) : (
                          <span className="w-5 h-5 sm:w-4 sm:h-4 rounded border-2 border-current opacity-40" />
                        )}
                      </button>

                      {/* Score Circle */}
                      <Link 
                        to={`/candidates/${candidate.id}`}
                        className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0"
                      >
                        <div
                          className={`score-circle shrink-0 ${getScoreCircleClass(
                            candidate.match_score?.overall_score || 0
                          )}`}
                        >
                          {candidate.match_score?.overall_score || 0}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-heading font-semibold truncate text-sm sm:text-base">
                              {candidate.data?.name || "Desconocido"}
                            </h3>
                            <Badge
                              className={`text-xs ${getMatchBadgeClass(
                                candidate.match_score?.match_level
                              )}`}
                            >
                              {candidate.match_score?.match_level || "Pendiente"}
                            </Badge>
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground truncate">
                            {candidate.file_name}
                          </p>
                          <div className="flex flex-wrap gap-1 sm:gap-1.5 mt-2">
                            {(candidate.data?.skills || [])
                              .slice(0, 3)
                              .map((skill) => (
                                <span
                                  key={skill}
                                  className={`skill-tag text-xs ${
                                    job.required_skills
                                      .map((s) => s.toLowerCase())
                                      .includes(skill.toLowerCase())
                                      ? "matched"
                                      : ""
                                  }`}
                                >
                                  {skill}
                                </span>
                              ))}
                            {(candidate.data?.skills || []).length > 3 && (
                              <span className="text-xs text-muted-foreground">
                                +{candidate.data.skills.length - 3} más
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Rank - hidden on very small screens */}
                        <div className="text-right shrink-0 hidden sm:block">
                          <span className="text-xs text-muted-foreground">
                            #{index + 1}
                          </span>
                        </div>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
              })}
            </div>
          )}
        </TabsContent>

        {/* Interviews Tab */}
        <TabsContent value="interviews">
          <InterviewCandidates
            jobId={jobId}
            job={job}
            allCandidates={candidates}
            onRefresh={fetchData}
          />
        </TabsContent>

        <TabsContent value="requirements">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Habilidades Requeridas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {job.required_skills.map((skill) => (
                  <span key={skill} className="skill-tag matched">
                    {skill}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {job.optional_skills.length > 0 && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="font-heading">
                  Habilidades Opcionales (Deseables)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {job.optional_skills.map((skill) => (
                    <span key={skill} className="skill-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Candidate Comparison Modal */}
      <CandidateCompare
        candidates={getSelectedCandidatesData()}
        job={job}
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
      />

      {/* Shortlist Recommendation Modal */}
      <ShortlistRecommendation
        jobId={jobId}
        candidates={candidates}
        job={job}
        open={shortlistOpen}
        onClose={() => setShortlistOpen(false)}
      />

      {/* Scoring Weights Editor Modal */}
      <ScoringWeightsEditor
        jobId={jobId}
        currentWeights={job?.scoring_weights}
        open={weightsEditorOpen}
        onClose={() => setWeightsEditorOpen(false)}
        onSave={handleWeightsUpdate}
      />
    </div>
  );
}
